import localforage from 'localforage';
import { v4 as uuidv4 } from 'uuid';
import { createAuditLog, AuditActionType } from './auditStore';
import { getDefaultSeedUsers, shouldUpdateSeedData } from './seedManager';

// Initialize stores
const userStore = localforage.createInstance({
  name: 'users',
});

let isInitialized = false;

// User roles and permissions
export const UserRole = {
  ADMIN: 'admin',
  EDITOR: 'editor',
  AUTHOR: 'author',
  MODERATOR: 'moderator',
  USER: 'user',
};

export const RolePermissions = {
  [UserRole.ADMIN]: ['*'],
  [UserRole.EDITOR]: [
    'blog.create',
    'blog.update',
    'blog.delete',
    'blog.publish',
    'comments.moderate',
  ],
  [UserRole.AUTHOR]: ['blog.create', 'blog.update', 'blog.delete.own'],
  [UserRole.MODERATOR]: ['comments.moderate', 'blog.read'],
  [UserRole.USER]: ['blog.read', 'comments.create'],
};

// Create a new user
export const createUser = async (userData, creatorId) => {
  const { username, email, role = UserRole.USER } = userData;

  // Validate required fields
  if (!username || !email) {
    throw new Error('Username and email are required');
  }

  // Check if username or email already exists
  const existingUsers = await getAllUsers();
  if (existingUsers.some(u => u.username === username)) {
    throw new Error('Username already exists');
  }
  if (existingUsers.some(u => u.email === email)) {
    throw new Error('Email already exists');
  }

  const now = new Date();

  const user = {
    id: uuidv4(),
    username,
    email,
    role,
    createdAt: now,
    updatedAt: now,
    lastLogin: null,
    status: 'active',
    twoFactorEnabled: false,
    twoFactorSecret: null,
  };

  await userStore.setItem(user.id, user);
  await createAuditLog(creatorId, AuditActionType.USER_CREATED, {
    userId: user.id,
    username: user.username,
    role: user.role,
  });

  return user;
};

// Update user
export const updateUser = async (id, updates, updaterId) => {
  const user = await userStore.getItem(id);
  if (!user) throw new Error('User not found');

  // Don't allow updating certain fields directly
  const allowedUpdates = updates;

  const updatedUser = {
    ...user,
    ...allowedUpdates,
    updatedAt: new Date(),
  };

  await userStore.setItem(id, updatedUser);
  await createAuditLog(updaterId, AuditActionType.USER_UPDATED, {
    userId: id,
    updates: Object.keys(allowedUpdates),
  });

  return updatedUser;
};

// Delete user
export const deleteUser = async (id, deleterId) => {
  const user = await userStore.getItem(id);
  if (!user) throw new Error('User not found');

  await userStore.removeItem(id);
  await createAuditLog(deleterId, AuditActionType.USER_DELETED, {
    userId: id,
    username: user.username,
  });

  return true;
};

// Get all users
export const getAllUsers = async () => {
  const keys = await userStore.keys();
  const users = await Promise.all(keys.map(key => userStore.getItem(key)));
  return users.map(user => ({ ...user }));
};

// Get user by ID
export const getUserById = async id => {
  const user = await userStore.getItem(id);
  if (!user) return null;
  return user;
};

// Update user role
export const updateUserRole = async (userId, newRole, updaterId) => {
  const user = await userStore.getItem(userId);
  if (!user) throw new Error('User not found');
  if (!Object.values(UserRole).includes(newRole)) {
    throw new Error('Invalid role');
  }

  const updatedUser = {
    ...user,
    role: newRole,
    updatedAt: new Date(),
  };

  await userStore.setItem(userId, updatedUser);
  await createAuditLog(updaterId, AuditActionType.USER_UPDATED, {
    userId,
    field: 'role',
    oldValue: user.role,
    newValue: newRole,
  });

  return updatedUser;
};

// Check user permissions
export const hasPermission = (user, permission) => {
  if (!user || !user.role) return false;
  const permissions = RolePermissions[user.role];
  return permissions.includes('*') || permissions.includes(permission);
};

// Enable/disable 2FA for user
export const updateUser2FAStatus = async (userId, enabled, secret = null) => {
  const user = await userStore.getItem(userId);
  if (!user) throw new Error('User not found');

  const updatedUser = {
    ...user,
    twoFactorEnabled: enabled,
    twoFactorSecret: secret,
    updatedAt: new Date(),
  };

  await userStore.setItem(userId, updatedUser);
  await createAuditLog(userId, AuditActionType.SECURITY_EVENT, {
    event: enabled ? '2FA_ENABLED' : '2FA_DISABLED',
  });

  return updatedUser;
};

// Initialize the authentication system
export const initializeAuthSystem = async () => {
  if (isInitialized) {
    return true;
  }

  try {
    // Check if we have any users
    const keys = await userStore.keys();

    if (keys.length === 0) {
      console.log('No users found, seeding with default users');

      // Get default seed users
      const defaultUsers = await getDefaultSeedUsers();

      // Store all users
      await Promise.all(
        defaultUsers.map(async user => {
          await userStore.setItem(user.id, user);
        })
      );

      console.log(`Initialized ${defaultUsers.length} default users`);
    }

    isInitialized = true;
    return true;
  } catch (error) {
    console.error('Error initializing auth system:', error);
    return false;
  }
};

export default userStore;
